import api from 'api/Assessor/evento';
import {
  Button,
  ButtonThemes,
  CloseIcon,
  Col,
  IconButton,
  Input,
  Row,
  SearchIcon,
  Select,
} from 'plataforma-fundacao-componentes';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { filtrosInitialState, filtrosReducer } from './reducer';
import styles from './styles.module.scss';

import { languageValues } from 'helpers/utils/languageUtils';

interface FilterParams {
  agencias?: string[];
  nucleos?: string[];
  cpfOrNome?: string;
}

const FILTERS_INITIAL_STATE: FilterParams = {
  agencias: [],
  nucleos: [],
  cpfOrNome: '',
};

interface Props {
  idEvento: number;
  onChangeFilter: (params: FilterParams) => void;
}

export default function Filtros({ idEvento, onChangeFilter }: Props) {
  const language = languageValues.pages.presents;

  const [
    { agencias, nucleos, cpfOrNome, listaAgencias, listaNucleos },
    dispatch,
  ] = useReducer(filtrosReducer, filtrosInitialState);
  const [stateFilters, setStateFilters] = useState<FilterParams>(
    FILTERS_INITIAL_STATE
  );

  const reducerFilters = useMemo<FilterParams>(() => {
    return {
      agencias,
      nucleos,
      cpfOrNome,
    };
  }, [agencias, nucleos, cpfOrNome]);

  const filtersChanged = useMemo<boolean>(() => {
    return JSON.stringify(reducerFilters) !== JSON.stringify(stateFilters);
  }, [reducerFilters, stateFilters]);

  const getAgencias = useCallback(async () => {
    const { fail, data } = await api.listarAgencias(idEvento);
    if (fail) {
      ErrorHandler(fail);
    }
    if (data) {
      dispatch({ type: 'setListaAgencias', value: data });
    }
  }, [idEvento]);

  const getNucleos = useCallback(() => {
    if (stateFilters.agencias && stateFilters.agencias.length === 1) {
      const agencia = listaAgencias.find((entry) => {
        return stateFilters.agencias![0] === entry.agencia;
      });
      dispatch({ type: 'setListaNucleos', value: agencia?.nucleos || [] });
    }
  }, [listaAgencias, stateFilters]);

  const handleAgenciaChange = useCallback(
    (event: any) => {
      const value = event;
      const needClearNucleos = value.length !== 1 && nucleos.length > 0;
      if (needClearNucleos) {
        setStateFilters({
          ...stateFilters,
          agencias: value,
          nucleos: [],
        });
      } else {
        setStateFilters({
          ...stateFilters,
          agencias: value,
        });
      }
    },
    [nucleos, setStateFilters, stateFilters]
  );

  const handleNucleoChange = useCallback(
    (event: any) => {
      setStateFilters({
        ...stateFilters,
        nucleos: event,
      });
    },
    [setStateFilters, stateFilters]
  );

  const handleCPFChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
        .replace(/[-/]/g, '')
        .replace(/\.(?=\d)/g, '');
      setStateFilters({
        ...stateFilters,
        cpfOrNome: value,
      });
    },
    [setStateFilters, stateFilters]
  );

  const eraseSearchFilter = useCallback(() => {
    setStateFilters({
      ...stateFilters,
      cpfOrNome: undefined,
    });
  }, [setStateFilters, stateFilters]);

  const toSearch = useCallback(() => {
    dispatch({ type: 'setAgencias', value: stateFilters.agencias });
    dispatch({ type: 'setNucleos', value: stateFilters.nucleos });
    dispatch({ type: 'setSearch', value: stateFilters.cpfOrNome });
    onChangeFilter({
      ...stateFilters,
      cpfOrNome:
        (stateFilters.cpfOrNome || '').length >= 3
          ? stateFilters.cpfOrNome
          : undefined,
    });
  }, [onChangeFilter, stateFilters]);

  useEffect(() => {
    getAgencias();
  }, [getAgencias]);

  useEffect(() => {
    getNucleos();
  }, [getNucleos]);

  return (
    <Row className={styles.root}>
      <Col cols={[6, 6, 3, 3, 3]}>
        <Select
          data-testid={'select-agency'}
          label={language.inputs.agencyLabel}
          options={listaAgencias!?.map((item: any) => {
            return {
              label: item.agencia,
              value: item.agencia,
            };
          })}
          onChange={handleAgenciaChange}
          placeholder={language.inputs.agencyPlaceholder}
          value={stateFilters.agencias || []}
          disabled={!listaAgencias.length}
          multiple
        />
      </Col>
      <Col cols={[6, 6, 3, 3, 3]}>
        <Select
          data-testid={'select-nucleou'}
          label={language.inputs.nucleouLabel}
          options={listaNucleos!?.map((item: any) => {
            return {
              label: item.nucleo,
              value: item.nucleo,
            };
          })}
          onChange={handleNucleoChange}
          placeholder={language.inputs.nucleouPlaceholder}
          value={stateFilters.nucleos}
          disabled={
            !listaNucleos.length || (stateFilters.agencias || []).length !== 1
          }
          multiple
        />
      </Col>
      <Col cols={[12, 12, 4, 4, 4]}>
        <Input
          data-testid={'input-name-or-cpf'}
          value={stateFilters.cpfOrNome || ''}
          label={language.inputs.nameOrCpfLabel}
          placeholder={language.inputs.nameOrCpfPlaceholder}
          onChange={handleCPFChange}
          rightObject={
            (stateFilters.cpfOrNome || '').trim().length > 0 ? (
              <IconButton icon={<CloseIcon />} onClick={eraseSearchFilter} />
            ) : undefined
          }
        />
      </Col>
      <Col cols={[12, 12, 2, 2, 2]} buttonActionsCol end>
        <Button
          data-testid={'search'}
          theme={ButtonThemes.Primary}
          leftIcon={<SearchIcon />}
          disabled={!filtersChanged}
          onClick={() => {
            if (filtersChanged) {
              toSearch();
            }
          }}
        >
          {language.buttons.search}
        </Button>
      </Col>
    </Row>
  );
}
